<template>
  <div class="googlemap">
    <div class="googlemap__container">
      <input
        v-show="model.state.settings.searchEnabled"
        ref="searchInputRef"
        class="googlemap__search"
        placeholder="Enter a location"
      />
      <div
        id="map"
        ref="googleMapRef"
        class="googlemap__item"
        :style="{ height: model.state.settings.height + 'px' }"
      />
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, nextTick, onMounted, ref, watch } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import type { AddonGoogleMapsModel, AddonGoogleMapsSettingsState } from '@/src/components/addons/googlemap/Model';
import { useCampaignStore } from '@/src/store/campaign';

interface Marker {
  id: string;
  marker: google.maps.Marker;
  infoWindow?: google.maps.InfoWindow;
}

interface MarkerData {
  addressLatLng: string;
  address?: string;
  infoWindow?: boolean;
  infoWindowAutoShow?: boolean;
  infoWindowContent?: string;
  // Any other properties used within createMarker and related functions
}

export default defineComponent({
  name: 'AddonGoogleMaps',
  props: {
    model: {
      type: Object as PropType<AddonGoogleMapsModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();
    const campaignModel = campaignStore?.model;
    const campaignState = campaignModel?.state;

    let loaderInstance: Loader | null = null;
    let campaignReloaded = false;
    let autoComplete: google.maps.places.Autocomplete | null = null;

    let map: google.maps.Map;
    let googleMapMarkers: Marker[] = [];

    const searchInputRef = ref<HTMLInputElement | null>(null);
    const googleMapRef = ref<HTMLElement | null>(null);
    let cluster: MarkerClusterer | undefined;

    const deconstructLatLng = (latLngString: string | undefined) => {
      return {
        lat: latLngString ? Number(latLngString.toString().split(',')[0]) : 0,
        lng: latLngString ? Number(latLngString.toString().split(',')[1]) : 0
      };
    };

    const setMapMarkerCluster = () => {
      const markers = googleMapMarkers.map((marker) => marker.marker);

      if (map && props.model.state.settings.cluster) {
        if (cluster) {
          cluster.clearMarkers();
          cluster.addMarkers(markers);
        } else {
          cluster = new MarkerClusterer(map, markers, {
            imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'
          });
        }
      } else if (cluster) {
        cluster.clearMarkers();
        // Ensure markers are added back to the map when the cluster is disabled
        markers.forEach((marker) => marker.setMap(map));
      }
    };

    const setMapZoomToFit = () => {
      if (!props.model.state.settings.zoomToFit) {
        return;
      }

      const markers = googleMapMarkers.map((marker) => marker.marker);

      const bounds = new google.maps.LatLngBounds();

      markers.forEach((marker) => {
        if (marker.getPosition()) {
          bounds.extend(marker.getPosition() as google.maps.LatLng);
        }
      });

      if (map) {
        map.fitBounds(bounds);
      }
    };

    const createSearchBox = () => {
      // early return if we have search already
      if (autoComplete) {
        return;
      }

      if (searchInputRef.value) {
        autoComplete = new google.maps.places.Autocomplete(searchInputRef.value as HTMLInputElement);

        autoComplete.bindTo('bounds', map);

        if (autoComplete) {
          autoComplete.addListener('place_changed', () => {
            const place = autoComplete?.getPlace();

            if (place && !place.geometry) {
              return;
            }

            if (map) {
              if (place?.geometry?.viewport) {
                map.fitBounds(place.geometry.viewport);
              } else if (place?.geometry && place.geometry.location && props.model.state.settings.zoom) {
                map.setCenter(place.geometry.location);
                map.setZoom(props.model.state.settings.zoom);
              }
            }
          });
        }
      }
    };

    const toggleInfoWindow = (infoWindow: google.maps.InfoWindow, marker: google.maps.Marker) => {
      if (!infoWindow) {
        return;
      }
      // @ts-ignore
      if (infoWindow.getMap()) {
        infoWindow.close();
      } else {
        infoWindow.setOptions({ disableAutoPan: true });
        infoWindow.open(map, marker);
      }
    };

    const setMapLocked = () => {
      map.set('draggable', !props.model.state.settings.locked);
      map.set('scrollwheel', !props.model.state.settings.locked);
    };

    const setMapConfig = () => {
      const center = new google.maps.LatLng(deconstructLatLng(props.model.state.settings.addressLatLng), true);

      if (googleMapRef.value) {
        if (map) {
          map.setCenter(center);
          map.setZoom(props.model.state.settings.zoom);
        } else {
          map = new google.maps.Map(googleMapRef.value as HTMLElement, {
            center,
            zoom: props.model.state.settings.zoom
          });
        }
      }

      setMapLocked();

      map.setOptions({
        mapTypeId: props.model.state.settings.type,
        styles:
          props.model.state.settings.displayColor && props.model.state.settings.displayColor === 'greyscale'
            ? [{ stylers: [{ saturation: -100 }] }]
            : []
      });

      createSearchBox();
    };

    const getMarkerData = (settings: AddonGoogleMapsSettingsState): MarkerData[] => {
      let markerData: MarkerData[] = [];

      // Handle single marker settings
      if (settings.marker) {
        markerData.push({
          address: settings.address,
          addressLatLng: settings.addressLatLng,
          infoWindow: settings.infoWindow,
          infoWindowAutoShow: settings.infoWindowAutoShow,
          infoWindowContent: settings.infoWindowContent
        });
      }

      // Handle multiple markers settings
      if (settings.markers && settings.markers.length > 0) {
        // @ts-ignore
        markerData = markerData.concat(settings.markers);
      }

      return markerData;
    };

    const setMapMarkers = () => {
      const allMarkerData = getMarkerData(props.model.state.settings);

      // Remove markers not included in the updated settings
      googleMapMarkers = googleMapMarkers.filter((marker) => {
        if (!allMarkerData.some((data) => data.addressLatLng === marker.id)) {
          marker.marker.setMap(null);
          marker.infoWindow?.close();
          return false;
        }
        return true;
      });

      let newMarkerCreated = false;
      // Update existing markers or add new ones
      allMarkerData.forEach((markerData) => {
        const existingMarker = googleMapMarkers.find((marker) => marker.id === markerData.addressLatLng);
        if (existingMarker) {
          updateMarker(existingMarker, markerData);
        } else {
          createMarker(markerData);
          newMarkerCreated = true;
        }
      });

      // Make sure cluster is updated after new markers are created
      if (newMarkerCreated) {
        setMapMarkerCluster();
        setMapZoomToFit();
      }
    };

    const updateMarker = (existingMarker: Marker, markerData: MarkerData) => {
      // Update the marker's position and icon
      existingMarker.marker.setPosition(deconstructLatLng(markerData.addressLatLng));
      existingMarker.marker.setIcon(props.model.state.settings.markerIcon || null);

      // Use the existing createOrUpdateInfoWindow function
      if (markerData.infoWindow && markerData.infoWindowContent) {
        createOrUpdateInfoWindow(existingMarker, markerData.infoWindowContent);
      } else if (existingMarker.infoWindow) {
        // If no info window is needed, close and remove it
        existingMarker.infoWindow.close();
        existingMarker.infoWindow = undefined;
      }
    };

    const createMarker = (markerData: MarkerData) => {
      if (!markerData.addressLatLng) {
        return;
      }

      const markerInstance: Marker = {
        marker: new google.maps.Marker({
          position: deconstructLatLng(markerData.addressLatLng),
          map,
          icon: props.model.state.settings.markerIcon || null
        }),
        infoWindow: undefined, // Initialize without an infoWindow
        id: markerData.addressLatLng
      };

      const markerPosition = markerInstance.marker.getPosition();
      if (markerPosition) {
        map.setCenter(markerPosition);
      }

      // Use the createOrUpdateInfoWindow function to handle the info window creation or update
      if (markerData.infoWindow && markerData.infoWindowContent) {
        createOrUpdateInfoWindow(markerInstance, markerData.infoWindowContent);
      }

      // If auto-show is enabled, open the info window
      if (markerData.infoWindowAutoShow && markerInstance.infoWindow) {
        markerInstance.infoWindow.open(map, markerInstance.marker);
      }

      googleMapMarkers.push(markerInstance);
    };

    const initMap = async () => {
      if (loaderInstance) {
        return;
      }

      if (!campaignState?.config?.googleMapsApiKey && !campaignReloaded) {
        await campaignModel?.reloadCampaign();
        campaignReloaded = true;
      }

      loaderInstance = new Loader({
        apiKey: campaignState?.config?.googleMapsApiKey ?? '',
        libraries: ['places']
      });

      await loaderInstance.load();
      await nextTick();
      await setMapConfig();
      await setMapMarkers();
    };

    const centerOnMarker = (latLng: string) => {
      if (map) {
        const newCenter = deconstructLatLng(latLng);
        map.setCenter(new google.maps.LatLng(newCenter.lat, newCenter.lng));
      }
    };

    watch(
      () => props.model.state.settings.locked,
      () => {
        setMapLocked();
      }
    );

    watch(
      () => props.model.state.settings.cluster,
      () => {
        setMapMarkerCluster();
      }
    );

    watch(
      () => props.model.state.settings.zoomToFit,
      () => {
        setMapZoomToFit();
      }
    );

    watch(
      () => props.model.state.settings.addressLatLng,
      (newLatLng) => {
        if (map) {
          centerOnMarker(newLatLng);

          if (props.model.state.settings.zoom) {
            map.setZoom(props.model.state.settings.zoom);
          }

          // Find the existing marker or create a new one if it doesn't exist
          const markerInstance = googleMapMarkers.find((marker) => marker.id === newLatLng);

          const markerData: MarkerData = {
            addressLatLng: newLatLng,
            infoWindow: props.model.state.settings.infoWindow,
            infoWindowAutoShow: props.model.state.settings.infoWindowAutoShow,
            infoWindowContent: props.model.state.settings.infoWindowContent || ''
          };

          if (markerInstance) {
            updateMarker(markerInstance, markerData);
          } else {
            createMarker(markerData);
          }
        }
      }
    );

    watch(
      () => props.model.state.settings.displayColor,
      () => {
        if (map) {
          map.setOptions({
            styles:
              props.model.state.settings.displayColor && props.model.state.settings.displayColor === 'greyscale'
                ? [{ stylers: [{ saturation: -100 }] }]
                : []
          });
        }
      }
    );

    watch(
      () => props.model.state.settings.type,
      () => {
        if (map) {
          map.setOptions({
            mapTypeId: props.model.state.settings.type
          });
        }
      }
    );

    const createOrUpdateInfoWindow = (markerInstance: Marker, content: string) => {
      if (!markerInstance.infoWindow) {
        // Create a new info window if it doesn't exist
        markerInstance.infoWindow = new google.maps.InfoWindow({
          content: `<div class="googlemap__infowindow">${content}</div>`
        });

        // Add a listener to toggle the info window on marker click
        markerInstance.marker.addListener('click', function () {
          toggleInfoWindow(markerInstance.infoWindow!, markerInstance.marker);
        });

        // Open the info window
        markerInstance.infoWindow.open(map, markerInstance.marker);
      } else {
        // Update the content of the existing info window
        markerInstance.infoWindow.setContent(`<div class="googlemap__infowindow">${content}</div>`);

        // Open the info window if it's not already open
        // @ts-ignore
        if (!markerInstance.infoWindow.getMap()) {
          markerInstance.infoWindow.open(map, markerInstance.marker);
        }
      }
    };

    const closeInfoWindow = (markerInstance: Marker) => {
      if (markerInstance.infoWindow) {
        markerInstance.infoWindow.close();
        markerInstance.infoWindow = undefined;
      }
    };

    watch(
      () => props.model.state.settings.infoWindowContent,
      () => {
        if (map) {
          const markerInstance = googleMapMarkers.find(
            (marker) => marker.id === props.model.state.settings.addressLatLng
          );

          if (markerInstance) {
            createOrUpdateInfoWindow(markerInstance, props.model.state.settings.infoWindowContent ?? '');
          }
        }
      }
    );

    watch(
      () => props.model.state.settings.infoWindow,
      (value) => {
        if (map) {
          const markerInstance = googleMapMarkers.find(
            (marker) => marker.id === props.model.state.settings.addressLatLng
          );

          if (markerInstance) {
            if (value) {
              createOrUpdateInfoWindow(markerInstance, props.model.state.settings.infoWindowContent ?? '');
            } else {
              closeInfoWindow(markerInstance);
            }
          }
        }
      }
    );

    watch(
      () => props.model.state.settings.markers,
      () => {
        if (props.model.state.settings.markers && props.model.state.settings.markers.length > 0) {
          setMapMarkers();
        } else {
          const allMarkerData = getMarkerData(props.model.state.settings);
          googleMapMarkers = googleMapMarkers.filter((marker) => {
            if (!allMarkerData.some((data) => data.addressLatLng === marker.id)) {
              marker.marker.setMap(null);
              marker.infoWindow?.close();
              return false;
            }
            return true;
          });

          centerOnMarker(props.model.state.settings.addressLatLng);
        }
      }
    );

    watch(
      () => props.model.state.settings.zoom,
      () => {
        if (map) {
          map.setZoom(props.model.state.settings.zoom);
        }
      }
    );

    onMounted(async () => {
      await initMap();
      await nextTick();
      await nextTick();
      centerOnMarker(props.model.state.settings.addressLatLng);
    });

    return {
      searchInputRef,
      googleMapRef
    };
  }
});
</script>

<style lang="scss">
.googlemap {
  &__container {
    position: relative;
  }

  &__search {
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0%);
    top: 1rem;
    z-index: 1;
    height: 4rem;
    min-width: 3.6rem;
    width: 50rem;
    padding: 0 1.7rem;
    border: 0 none;
    color: #000;
    font-family: Roboto, Arial, sans-serif;
    user-select: none;
    font-size: 1.8rem;
    background-color: #fff;
    border-radius: 2px;
    box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;

    &::placeholder {
      color: rgb(86, 86, 86);
      opacity: 1;
    }

    &:-ms-input-placeholder {
      color: rgb(86, 86, 86);
    }

    &::-ms-input-placeholder {
      color: rgb(86, 86, 86);
    }

    .site--tablet & {
      width: 40rem;
    }

    .site--mobile & {
      width: calc(100% - 20px);
    }
  }

  .googlemap__item {
    display: block;
    color: #000000;
    height: 300px;

    img {
      margin-bottom: 0.5rem;
    }

    .gm-style-iw {
      padding: 0.5rem 0 0 0;
    }
  }
  .googlemap__infowindow {
    padding: 0 var(--gutter);
  }
}
</style>
